import { BRAND } from "constants/brand";
import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faFileInvoiceDollar } from "@fortawesome/free-solid-svg-icons";

import { Form } from "react-bootstrap";
import DateTimeField from "components/Ui/DateTimeField/DateTimeField";
import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";

import {
    useCreateStatement,
    usePreviewStatement,
    useStatementPendingRange,
} from "hooks/useStatements";
import { formatStatNumber, parsePercentageInput } from "utils/percentage";
import { formatMoney } from "utils/money";
import { formatDateTime } from "utils/dateFormat";

/**
 * Modal that lets an admin compose, preview, and send a vendor account
 * statement (كشف حساب). It re-fetches the preview from the server every
 * time the inputs change, so the totals shown to the admin always reflect
 * the exact filter the create endpoint will apply (and the rendered
 * WhatsApp body is the server-rendered string — single source of truth).
 *
 * Props:
 * - show:      whether the modal is open
 * - close:     close callback
 * - vendor:    selected vendor object {id, name, order_percentage}; required
 * - cityId:    optional, used as backstop for create payload (server resolves from vendor)
 * - showMessage(severity, head, body): toast helper from parent
 * - initialStartDate / initialEndDate: optional ISO strings to prefill the date inputs
 *
 * THE DEFAULT RANGE COVERS EVERYTHING OUTSTANDING. "من" opens on the vendor's
 * earliest unbilled order (GET /api/statements/pending-range), not on a fixed
 * lookback, so sending without touching the dates settles the whole outstanding
 * balance. `initialStartDate` from the orders page still wins when present.
 *
 * That window is self-limiting: the statement query excludes orders already
 * carrying a `vendor_statement_id`, so "earliest unbilled" moves forward every
 * time a statement is sent rather than reaching back to the vendor's first ever
 * order. A hint under the field states the date and the outstanding count, so
 * narrowing the range is a visible choice rather than a silent omission.
 */
const SendVendorStatementModal = (props) => {
    const nodeRef = useRef();

    const previewMutation = usePreviewStatement();
    const createMutation = useCreateStatement();

    /**
     * The vendor's outstanding period — the oldest order they still owe a
     * statement for, and how many there are. Only fetched while the modal is
     * open, and never cached across opens: issuing a statement archives the
     * orders it covered, so the range moves as soon as one is sent.
     */
    const {
        earliestOrderDate,
        pendingCount,
        loading: rangeLoading,
    } = useStatementPendingRange(props.vendor?.id, props.show);

    /**
     * "من" opens on the EARLIEST UNBILLED ORDER, so the default statement covers
     * everything outstanding.
     *
     * It used to open 30 days back, which silently excluded anything older that
     * had never been billed — the admin saw a plausible total, sent it, and those
     * orders stayed outstanding with nothing on screen saying they were skipped.
     * Nothing narrows to a lookback now: already-billed orders are excluded by the
     * statement query itself, so "everything outstanding" is a window that shrinks
     * on its own as statements go out.
     *
     * Precedence: an explicit range from the orders page beats the earliest order
     * (an operator who filtered a period means that period). The 30-day value
     * survives only as the fallback for a vendor with nothing outstanding, where
     * there is no earliest order to open on.
     */
    const defaultStartDate = useMemo(() => {
        if (props.initialStartDate) {
            return toDateTimeLocalValue(props.initialStartDate);
        }
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return toDateTimeLocalValue(d);
    }, [props.initialStartDate]);

    const defaultEndDate = useMemo(() => {
        if (props.initialEndDate) {
            return toDateTimeLocalValue(props.initialEndDate);
        }
        return toDateTimeLocalValue(new Date());
    }, [props.initialEndDate]);

    // THE PRE-FILLED RATE IS THE SERVER'S, NOT THIS APP'S. It used to fall back
    // to a DEFAULT_VENDOR_PERCENTAGE constant kept here, which did not match the
    // data — 7% is the most common negotiated rate and that constant was 5. The
    // API now resolves the restaurant's own rate or its city's default and
    // sends it as `effective_order_percentage`.
    //
    // Empty rather than a guess when the server sent nothing: this box decides
    // what a restaurant is billed, so an operator should type it rather than
    // accept a number this screen invented.
    const defaultPercentage = useMemo(() => {
        const fromVendor = parsePercentageInput(props.vendor?.order_percentage);
        const fromServer = parsePercentageInput(props.vendor?.effective_order_percentage);
        const resolved = fromVendor ?? fromServer;
        return resolved === null ? "" : String(resolved);
    }, [props.vendor?.order_percentage, props.vendor?.effective_order_percentage]);

    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [percentage, setPercentage] = useState(defaultPercentage);
    // Toggle defaults to ON on every open — keep it locally scoped to
    // this modal; no parent prop to inherit from anymore.
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState(null);

    // Reset form when the modal opens against a different vendor / dates.
    useEffect(() => {
        if (!props.show) {
            return;
        }
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
        setPercentage(defaultPercentage);
        setPreview(null);
        setPreviewError(null);
    }, [props.show, defaultStartDate, defaultEndDate, defaultPercentage]);

    /**
     * Widen "من" to the earliest unbilled order once the range lands. Separate
     * from the reset above because it arrives asynchronously — the form is
     * already interactive by then.
     *
     * Applied at most ONCE per open, guarded by the ref, so an admin who edits
     * the field while the request is still in flight doesn't have their value
     * yanked out from under them by a late response.
     */
    const rangeAppliedForRef = useRef(null);
    useEffect(() => {
        if (!props.show) {
            rangeAppliedForRef.current = null;
            return;
        }
        // An explicit range from the orders page wins — see defaultStartDate.
        if (props.initialStartDate || !earliestOrderDate) {
            return;
        }
        const key = String(props.vendor?.id);
        if (rangeAppliedForRef.current === key) {
            return;
        }
        rangeAppliedForRef.current = key;
        setStartDate(toDateTimeLocalValue(earliestOrderDate));
    }, [props.show, props.initialStartDate, props.vendor?.id, earliestOrderDate]);

    /**
     * Refetch the preview whenever the inputs settle. We debounce lightly
     * so typing in the percentage box doesn't fire on every keystroke.
     */
    useEffect(() => {
        if (!props.show || !props.vendor?.id) {
            return undefined;
        }
        const parsedPct = parsePercentageInput(percentage);
        if (parsedPct === null || !startDate || !endDate) {
            setPreview(null);
            return undefined;
        }

        const timer = setTimeout(() => {
            previewMutation.mutate(
                {
                    audience: "vendor",
                    vendorId: props.vendor.id,
                    // Sent as the wall clock the operator picked. The API
                    // compares these against a bare MySQL datetime, so an
                    // ISO round trip through UTC could only lose — and it
                    // cost the range its inclusive last day.
                    startDate,
                    endDate,
                    percentage: parsedPct,
                },
                {
                    onSuccess: (data) => {
                        setPreview(data);
                        setPreviewError(null);
                    },
                    onError: (err) => {
                        setPreview(null);
                        setPreviewError(
                            err?.response?.data?.message || err?.message || "تعذر جلب المعاينة",
                        );
                    },
                },
            );
        }, 350);

        return () => clearTimeout(timer);
        // previewMutation is intentionally omitted: react-query returns a
        // stable `.mutate` reference, and including the whole mutation
        // object would re-fire the effect on every render.
    }, [props.show, props.vendor?.id, startDate, endDate, percentage]);

    const handleConfirm = async () => {
        const parsedPct = parsePercentageInput(percentage);
        if (parsedPct === null) {
            props.showMessage?.("error", "نسبة غير صالحة", "النسبة يجب أن تكون بين 0 و 100");
            return;
        }
        if (!preview || preview.ordersCount === 0) {
            props.showMessage?.(
                "error",
                "لا يوجد طلبات",
                "لا يوجد طلبات في الفترة المختارة لإرسال كشف عنها",
            );
            return;
        }

        try {
            const data = await createMutation.mutateAsync({
                audience: "vendor",
                vendorId: props.vendor.id,
                startDate,
                endDate,
                percentage: parsedPct,
            });
            const sendOk = data?.statement?.whatsapp_send_status === "sent";
            props.showMessage?.(
                sendOk ? "success" : "warn",
                sendOk ? "تم الإرسال" : "تم الإنشاء",
                sendOk
                    ? "تم إرسال كشف الحساب على الواتساب وأرشفة الطلبات"
                    : "تم إنشاء الكشف وأرشفة الطلبات، لكن فشل إرسال الواتساب — استخدم زر إعادة الإرسال",
            );
            props.close();
        } catch (err) {
            props.showMessage?.(
                "error",
                "تعذر الإرسال",
                err?.response?.data?.message || err?.message || "حدث خطأ غير متوقع",
            );
        }
    };

    const isLoading = previewMutation.isPending || createMutation.isPending;
    const canConfirm =
        Boolean(preview) && preview.ordersCount > 0 && !createMutation.isPending && !isLoading;

    return (
        <div className={`model ${props.show ? "model-show" : ""}`} onClick={props.close}>
            <CSSTransition
                mountOnEnter
                unmountOnExit
                nodeRef={nodeRef}
                in={props.show}
                timeout={400}
                classNames="show"
            >
                <div className="addItem" ref={nodeRef} onClick={(e) => e.stopPropagation()}>
                    {isLoading ? (
                        <div className="loading-spinner center">
                            <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                        </div>
                    ) : null}
                    <div className="head center">
                        <h5>إرسال كشف حساب</h5>
                        <FontAwesomeIcon icon={faClose} onClick={props.close} />
                    </div>

                    <Form
                        className="form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleConfirm();
                        }}
                    >
                        <Form.Group className="mb-3">
                            <Form.Label>
                                المطعم<span>*</span>
                            </Form.Label>
                            <Form.Control type="text" value={props.vendor?.name ?? ""} disabled />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                من<span>*</span>
                            </Form.Label>
                            <DateTimeField value={startDate} onChange={setStartDate} edge="start" />
                            {/* Says what the default covers, so an admin who narrows
                                the range knows what they are leaving behind. */}
                            {rangeLoading ? (
                                <Form.Text muted>جاري تحديد أقدم طلب غير محتسب…</Form.Text>
                            ) : earliestOrderDate ? (
                                <Form.Text muted>
                                    أقدم طلب غير محتسب:{" "}
                                    <bdi dir="ltr">{formatDateTimeLabel(earliestOrderDate)}</bdi> (
                                    {formatStatNumber(pendingCount)} طلب غير محتسب)
                                </Form.Text>
                            ) : (
                                <Form.Text muted>لا يوجد طلبات غير محتسبة لهذا المطعم</Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                إلى<span>*</span>
                            </Form.Label>
                            <DateTimeField value={endDate} onChange={setEndDate} edge="end" />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                نسبة العمولة %<span>*</span>
                            </Form.Label>
                            <Form.Control
                                type="number"
                                min={0}
                                max={100}
                                step={0.01}
                                value={percentage}
                                onChange={(e) => setPercentage(e.target.value)}
                            />
                        </Form.Group>

                        <div className="stats-container2" style={{ marginBottom: "20px" }}>
                            <div className="stat-box">
                                <h4>عدد الطلبات</h4>
                                <p>{formatStatNumber(preview?.ordersCount ?? 0)}</p>
                            </div>
                            <div className="stat-box">
                                <h4>إجمالي المبيعات (₪)</h4>
                                <p>{formatMoney(preview?.subtotal ?? 0)}</p>
                            </div>
                            <div className="stat-box">
                                <h4>عمولة التطبيق (₪)</h4>
                                <p>{formatMoney(preview?.commission ?? 0)}</p>
                            </div>
                            <div className="stat-box">
                                <h4>الصافي المستحق (₪)</h4>
                                <p>{formatMoney(preview?.netAmount ?? 0)}</p>
                            </div>
                        </div>

                        {previewError ? (
                            <div style={{ color: "red", marginBottom: "12px" }}>{previewError}</div>
                        ) : null}

                        {preview?.vendorPhone ? (
                            <div style={{ marginBottom: "12px" }}>
                                <strong>سيتم الإرسال على:</strong> {preview.vendorPhone}
                            </div>
                        ) : null}

                        {preview?.whatsappMessage ? (
                            <Form.Group className="mb-3">
                                <Form.Label>نص رسالة الواتساب</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={9}
                                    value={preview.whatsappMessage}
                                    readOnly
                                />
                            </Form.Group>
                        ) : null}

                        <div className="buttons">
                            <button className="cancel" onClick={props.close} type="button">
                                إلغاء
                            </button>
                            <button
                                className="button"
                                type="submit"
                                disabled={!canConfirm}
                                style={{
                                    opacity: canConfirm ? 1 : 0.6,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                }}
                            >
                                <FontAwesomeIcon icon={faFileInvoiceDollar} />
                                إرسال كشف الحساب
                            </button>
                        </div>
                    </Form>
                </div>
            </CSSTransition>
        </div>
    );
};

/**
 * Convert a Date / ISO string into the value an <input type="datetime-local">
 * expects: "YYYY-MM-DDTHH:mm" in the local timezone.
 */
/**
 * A short human-readable date+time for the hint line under "من". Arabic-Latin
 * digits (ar-EG would render Eastern Arabic numerals, which the rest of this
 * dashboard does not use).
 */
function formatDateTimeLabel(value) {
    return formatDateTime(value) ?? "";
}

function toDateTimeLocalValue(value) {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) {
        return "";
    }
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default SendVendorStatementModal;
